// Sales money display + the area's ONE omission-redaction rule — pure,
// unit-tested.
//
// `formatMinor` is a RE-EXPORT of the inventory helper (cross-area lib reuse
// precedent: waste's remnants screen imports production's format lib) — the
// money rule is repo-wide (integer minor units, the currency's own exponent,
// the only float is the final divide for presentation), and a third copy is
// how two screens end up printing one price two ways.
//
// REDACTION IS OMISSION here (spec 09 §7 literal: "redacted keys are omitted
// from the payload, never null, never a marker") — NOT module-06's
// `redacted: true` marker style, so inventory's `costPresence` does NOT
// transplant. The sales rule, stated once:
//
//   * a money/margin COLUMN renders iff ≥ 1 row of the current page carries
//     the key (`columnPresent`) — a column of nothing is the empty column the
//     doctrine forbids;
//   * a rendered column's absent cell renders NOTHING (an empty fragment) —
//     never a dash, because "—" is the register's "there is no such thing"
//     and a redacted value is a thing this role may not see.

import { formatMinor } from "../../inventory/lib/cost";
import { cutBefore, oneRun, type TwoRuns } from "../../../../lib/two-runs";

export { formatMinor };

/**
 * `formatMinor`'s text cut between the amount and its currency (`TwoRuns`,
 * lib/two-runs.ts), so a `fit` table's narrow money cell may put the currency
 * on its own line while the amount stays whole (design-system §3.3, D-336):
 * ru «8 000 000,00» over «KZT», en «KZT» over «8,000,000.00». The cut is the
 * whitespace beside the currency part, on whichever side the locale writes
 * it. A text with no such whitespace (en-US «$12.50»), or one `formatMinor`
 * wrote from its fallback, comes back as one run.
 */
export function formatMinorRuns(minor: number, currency: string, locale?: string): TwoRuns {
  const whole = formatMinor(minor, currency, locale);
  let parts: Intl.NumberFormatPart[];
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    parts = fmt.formatToParts(minor / Math.pow(10, digits));
  } catch {
    return oneRun(whole);
  }
  const at = parts.findIndex((p) => p.type === "currency");
  if (at < 0) return oneRun(whole);
  const lengthBefore = (end: number): number =>
    parts.slice(0, end).reduce((length, p) => length + p.value.length, 0);
  const values = parts.map((p) => p.value);
  if (at === parts.length - 1) return cutBefore(whole, values, lengthBefore(at));
  if (at === 0) {
    // The currency leads, and the amount is the second run, after the
    // literal that separates them.
    return cutBefore(whole, values, lengthBefore(parts[1]?.type === "literal" ? 2 : 1));
  }
  return oneRun(whole);
}

/**
 * Should a page of rows render this omission-redacted column at all?
 * `pick` reads the optional wire key; `undefined` is the omission.
 */
export function columnPresent<T>(rows: readonly T[], pick: (row: T) => unknown): boolean {
  for (const row of rows) {
    if (pick(row) !== undefined) return true;
  }
  return false;
}

/**
 * The minor-unit exponent of a currency (KZT → 2, JPY → 0), read off Intl —
 * the same resolution `formatMinor` uses, so an import parsed at 2 dp and a
 * price rendered at 2 dp cannot disagree. Falls back to 2 when the runtime
 * does not know the code.
 */
export function minorUnitDigits(currency: string, locale?: string): number {
  try {
    return (
      new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * Integer minor units → canonical decimal MAJOR text ("125050", 2 → "1250.50")
 * — string arithmetic only, the editable-grid inverse of
 * `price-import.decimalToMinor`. Non-negative inputs only (a price).
 */
export function minorToDecimal(minor: number, digits: number): string {
  const text = Math.trunc(Math.abs(minor)).toString();
  if (digits <= 0) return text;
  const padded = text.padStart(digits + 1, "0");
  const whole = padded.slice(0, -digits);
  const frac = padded.slice(-digits);
  return `${whole}.${frac}`;
}

// ---------------------------------------------------------------------------
// TYPED DECIMAL → CANONICAL DECIMAL (task #204)
//
// THE FAILURE THIS EXISTS FOR. Every money box in this area parses with
// `decimalToMinor`, whose grammar is `^\d{1,15}(\.\d+)?$` — a dot, no
// separators. But the screens RENDER money with `formatMinor`, which in the
// design partner's locale prints «45 000,00 ₸». A KazKabel manager reads that,
// negotiates down, types `44 000,00` from the keyboard in front of them, and is
// refused. The save's floor holds (d5f81ef — a price that does not parse is
// never treated as `manual`), so no money is lost; the manager is simply told
// no, by a screen that showed them that exact shape a second earlier.
//
// WHY NOT JUST WIDEN `decimalToMinor`. Two reasons, both load-bearing:
//   1. It is the CANONICAL-FORM parser and is also the quantity parser
//      (`qtyToMilli` calls it at 3 dp). Widening it would loosen every caller
//      at once, including ones that read machine-produced text.
//   2. `parsePriceTable`'s CSV path must NOT become locale-dependent: a
//      pasted file may be in any locale, and guessing per-cell is how an
//      import silently multiplies a column by a thousand.
// So the locale knowledge lives HERE, at the typed-input boundary, and
// `decimalToMinor` stays exactly as strict.
//
// THE RULE THAT KEEPS IT SAFE. A group separator is stripped ONLY where it is
// grammatically a group separator — `^\d{1,3}(<group>\d{3})*$`. That is not
// pedantry, it is the difference between a refusal and a hundredfold error: in
// `en` the group separator is a comma, so a naive strip turns `45000,00` into
// `4500000`. Under this rule that text fails the grouping check, is returned
// UNCHANGED, and `decimalToMinor` refuses it — the manager gets an error
// instead of a price two orders of magnitude wrong.
//
// AMBIGUITY IS RESOLVED FROM THE ACTIVE LOCALE, never from the string's shape.
// A dot is additionally accepted as the decimal point when the locale does not
// use it for grouping (true for en/ru/kk/fr/zh — ru and fr group with a
// no-break space), because numeric keypads emit a dot whatever the UI language.
// Where a locale groups with dots, that acceptance is withheld and only the
// locale's own decimal separator counts.
//
// THE HAZARD RUNS BOTH WAYS, and the en case above is only the louder one. In
// a space-grouping locale the dot is the decimal point, so at a currency with
// THREE minor digits — KWD, BHD, OMR, TND — a manager typing `45.000` means
// 45 and gets 45, while the same keystrokes in en mean 45 000. That is the rule
// working as specified rather than a defect, and it is stated here because the
// argument above only justifies refusing the 100× direction: the 1000× one is
// resolved, correctly, by the locale rather than by the currency's exponent.
// A locale-correct `45 000` is still available in every one of those locales.

interface Separators {
  readonly group: string;
  readonly decimal: string;
}

/** What `Intl` says this locale groups and points with. Cached: the lookup is
 * the expensive half and a screen re-renders constantly. */
const separatorCache = new Map<string, Separators>();

function separatorsOf(locale: string): Separators {
  const hit = separatorCache.get(locale);
  if (hit !== undefined) return hit;
  let found: Separators = { group: ",", decimal: "." };
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const group = parts.find((p) => p.type === "group")?.value;
    const decimal = parts.find((p) => p.type === "decimal")?.value;
    found = { group: group ?? ",", decimal: decimal ?? "." };
  } catch {
    // An unknown locale tag keeps the en-shaped default rather than throwing:
    // a money box that cannot parse at all is worse than one that parses the
    // canonical form.
  }
  separatorCache.set(locale, found);
  return found;
}

/**
 * ANY whitespace, where a locale groups with a space.
 *
 * It was an enumeration — U+0020, U+00A0, U+202F — and an adversarial review
 * showed why that is the wrong shape. Modern ICU uses U+202F for fr and U+00A0
 * for ru, but CLDR shipped **U+2009** for fr before CLDR 34: a runtime on an
 * older ICU would PRINT `45 000,00` with a thin space and then refuse the
 * ordinary space the user types back — silently re-creating this exact bug on
 * one of the two launch markets. Enumerating the separators a runtime happens
 * to use today is a bet on the runtime.
 *
 * Widening is SAFE IN ONE DIRECTION ONLY, which is why it is allowed here:
 * accepting more space-ish characters can only make more strings reach the
 * grouping check, and that check still has to match `D{1,3}(GDDD)+` before any
 * separator is removed. A mis-grouped string is refused exactly as before; no
 * string that used to be refused can become a different NUMBER.
 *
 * `\s` is written as an escape rather than a literal class: a bare no-break
 * space is invisible in every diff, and `no-irregular-whitespace` is right to
 * refuse one in a regex whose job is telling look-alike spaces apart.
 */
const SPACEY = /\s/gu;

/** MUST NOT become `SPACEY.test(ch)`. `SPACEY` is `g`-flagged and shared, so
 * `.test()` would advance `lastIndex` and return alternating answers on the
 * same input; `.match()` and `.replace()`, the only two uses, both reset it. */
function isSpacey(ch: string): boolean {
  return /^\s$/u.test(ch);
}

/**
 * What the manager typed → the canonical `1234.56` `decimalToMinor` accepts.
 *
 * Returns the input UNCHANGED whenever the text is not confidently
 * interpretable — a second decimal point, a mis-grouped separator, a stray
 * letter. Callers keep parsing with `decimalToMinor`, so "not confidently
 * interpretable" and "invalid" land in the same place: refused, never guessed.
 */
export function normalizeTypedDecimal(text: string, locale: string): string {
  const raw = text.trim();
  if (raw.length === 0) return raw;
  const { group, decimal } = separatorsOf(locale);

  // A dot also points, unless this locale groups with dots.
  const decimals = new Set<string>([decimal]);
  if (group !== ".") decimals.add(".");

  // UTF-16 indexes, deliberately: `raw.slice(at + 1)` below is UTF-16, so a
  // code-point index from spreading the string would cut in the wrong place
  // the moment any astral character appeared before the separator.
  const positions: number[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    if (decimals.has(raw.charAt(i))) positions.push(i);
  }
  // Two points is not a number. Belt-and-braces rather than load-bearing: the
  // fraction check below already refuses these, because a second separator
  // leaves a non-digit in the fraction. Kept so a later refactor of that check
  // cannot quietly take this rule with it.
  if (positions.length > 1) return raw;
  const at = positions[0];
  const whole = at === undefined ? raw : raw.slice(0, at);
  const frac = at === undefined ? "" : raw.slice(at + 1);
  if (frac.length > 0 && !/^\d+$/u.test(frac)) return raw;

  // The integer half may carry group separators — and only in the right places.
  const groupChars = isSpacey(group) ? whole.match(SPACEY) : whole.includes(group) ? [group] : null;
  if (groupChars === null || groupChars.length === 0) {
    return /^\d+$/u.test(whole) ? (frac.length > 0 ? `${whole}.${frac}` : whole) : raw;
  }
  const normalisedWhole = isSpacey(group) ? whole.replace(SPACEY, " ") : whole;
  const sep = isSpacey(group) ? " " : group;
  const grouped = new RegExp(
    `^\\d{1,3}(?:${sep.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}\\d{3})+$`,
    "u",
  );
  if (!grouped.test(normalisedWhole)) return raw; // `45000,00` in en lands here
  const digits = normalisedWhole.split(sep).join("");
  return frac.length > 0 ? `${digits}.${frac}` : digits;
}
